import { Middleware } from 'redux';
import { FILTER, INVOICE, INVOICE_TEMP } from 'core/datasets/action';
import { getFilterValue, ORDER_BY, ORDER_DIR } from 'core/hooks/useQueryHelper';
import PAGE_CONTEXT from 'core/datasets/page_context';
import { invoiceActions } from '../invoiceActions';
import { queryAllInvoicesDocument } from 'invoice/api/gql/useQueryInvoices/gql/queryAllInvoicesDocument';
import { IInvoiceListViewData } from 'invoice/types/IInvoiceListViewData';
import { IInvoiceData } from 'invoice/types/IInvoiceData';
import { invoicePropsDataToInvoiceData } from 'invoice/utils/invoiceApiMapper';
import { InvoiceListViewItem } from 'invoice/classes/InvoiceListViewItem';
import { environmentSelectors } from 'environment/redux/environmentSelectors';
import { AppState } from 'core/redux/rootReducer';
import { getInvoicePdfGqlDocument } from 'invoice/gql/mutationInvoicePdf';
import { InvoicePdf } from 'invoice/classes/InvoicePdf';
import { Browser } from '@capacitor/browser';

async function fetchFile(url: string): Promise<Blob> {
    try {
        const response = await fetch(url);
        return response.blob();
    } catch (e) {
        throw new Error('Failed to fetch');
    }
}

const invoiceMiddleware: Middleware =
    ({ dispatch, getState }) =>
    (next) =>
    (action) => {
        next(action);

        const state: AppState = getState();

        const fetchAllInvoices = () => {
            const QUERY_LIMIT = 200;
            const QUERY_OFFSET = 0;

            const variables = {
                orderDir: ORDER_DIR.desc,
                orderBy: ORDER_BY.invoiceDate,
                limit: QUERY_LIMIT,
                offset: QUERY_OFFSET,
                filterBy: getFilterValue(PAGE_CONTEXT.INVOICES),
            };

            dispatch(invoiceActions.fetchAllInvoices(queryAllInvoicesDocument, variables));
        };

        switch (action?.type) {
            case INVOICE.FETCH_ALL_INVOICES: {
                fetchAllInvoices();
                break;
            }

            case FILTER.CHANGE_FILTER: {
                fetchAllInvoices();
                break;
            }

            case FILTER.RESET_FILTER_GROUP: {
                fetchAllInvoices();
                break;
            }

            case INVOICE.ALL_INVOICES_RECEIVED: {
                const invoices = action.payload.data.invoices;
                const isPayment = environmentSelectors.invoiceAsPaymentNotification(state);

                const list: IInvoiceListViewData[] = [];
                const mapData: { [id: number]: IInvoiceData } = {};

                let defaultSelectedInvoice: IInvoiceData | null = null;

                for (const data of invoices) {
                    const invoice = invoicePropsDataToInvoiceData(data);
                    list.push(new InvoiceListViewItem(invoice, Boolean(isPayment)).listViewData);
                    mapData[invoice.id] = invoice;

                    if (defaultSelectedInvoice === null) {
                        defaultSelectedInvoice = invoice;
                    }
                }

                dispatch(invoiceActions.updateInvoicesData({ list, map: mapData }));
                break;
            }

            case INVOICE_TEMP.REQUEST_PDF: {
                const invoiceId = action.payload;
                dispatch(invoiceActions.fetchInvoicePdf(getInvoicePdfGqlDocument, { id: invoiceId }));
                break;
            }

            case INVOICE_TEMP.PDF_DATA_RECEIVED: {
                const invoicePdfData = action.payload.data?.invoicePdf;

                if (!invoicePdfData) {
                    console.error('[handleReceivedInvoicePdf]: Failed to fetch invoice PDF data');
                    return;
                }

                const selectedInvoiceId = state.tempInvoices.currentInvoiceId;

                if (!selectedInvoiceId) {
                    console.log('[handleReceivedInvoicePdf saga]: undefined selectedInvoiceId');
                    return;
                }

                const invoicePdf = new InvoicePdf({ ...invoicePdfData, invoiceId: selectedInvoiceId });
                dispatch(invoiceActions.storeInvoicePdf(invoicePdf.all));

                if (invoicePdf?.file?.url) {
                    fetchFile(invoicePdf.file.url)
                        .then((pdfBlob) => {
                            const pdfUrl = URL.createObjectURL(pdfBlob);
                            dispatch(invoiceActions.storePdfBLobUrl(pdfUrl));
                        })
                        .catch((e) => {
                            console.error('[handleReceivedInvoicePdf]: Failed to fetch PDF blob!, ', e);
                        });
                    return;
                }

                if (invoicePdf.content) {
                    dispatch(invoiceActions.convertBase64PdfDataToUrl(invoicePdf.content));
                }
                break;
            }

            case INVOICE_TEMP.PDF_FILE_OPENED_ON_ANDROID_PLATFORM: {
                const pdfData = action.payload;

                if (!pdfData.fileUrl) {
                    const replaceAll = (text: string, search: RegExp, replacer: string) => {
                        const globalSearch = new RegExp(search, 'g');
                        return text.replace(globalSearch, replacer);
                    };
                    const cleanFileName = replaceAll(pdfData.name, /\W/gi, '_');
                    const cleanPdfName = `${cleanFileName}.pdf`;

                    //@ts-ignore
                    window.PreviewAnyFile.previewBase64(null, null, `data:application/pdf;base64, ${pdfData.content}`, {
                        name: cleanPdfName,
                    });
                    return;
                }

                const id = state.tempInvoices.currentInvoiceId;
                const isActiveUrl = InvoicePdf.isLinkALive(pdfData.receivedAt, pdfData.fileLifetime || 60);

                if (!id) {
                    console.error('[handleOpeningInvoicePdfOnNativePlatform]: No invoice id!');
                    return;
                }

                if (!isActiveUrl || !pdfData?.fileUrl) {
                    dispatch(invoiceActions.requestPdf(Number(id)));
                    return;
                }

                Browser.open({ url: pdfData.fileUrl });
                break;
            }

            default:
                break;
        }
    };

export default invoiceMiddleware;
